import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminUser } from '../types/admin';
import { toast } from 'sonner';
import { logUserLogin, logUserLogout } from '@/services/audit/authAuditService';

const WELCOMED_KEY = 'admin-welcomed';

const getWelcomedStatus = () => sessionStorage.getItem(WELCOMED_KEY) === 'true';
const setWelcomedStatus = (value: boolean) => {
  if (value) {
    sessionStorage.setItem(WELCOMED_KEY, 'true');
  } else {
    sessionStorage.removeItem(WELCOMED_KEY);
  }
};

export const useAdminAuth = () => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isCheckingRef = useRef(false);
  const lastCheckedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const handleSession = async (userId: string | undefined) => {
      if (!isMounted) return;
      if (!userId) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      await checkAdminStatus(userId);
    };

    // Listener — processar INITIAL_SESSION e SIGNED_IN
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔔 Admin auth state change:', event, session?.user?.id);

      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        handleSession(session?.user?.id);
      } else if (event === 'SIGNED_OUT') {
        console.log('❌ User signed out - clearing admin state');
        setUser(null);
        setWelcomedStatus(false);
        lastCheckedUserIdRef.current = null;
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkAdminStatus = async (userId: string) => {
    if (isCheckingRef.current || lastCheckedUserIdRef.current === userId) {
      console.log('⏭️ Skipping duplicate checkAdminStatus call');
      return;
    }

    isCheckingRef.current = true;
    lastCheckedUserIdRef.current = userId;

    try {
      const { data: isAdmin, error: rpcError } = await supabase.rpc('is_platform_admin', {
        _user_id: userId
      });

      if (rpcError) {
        console.error('❌ Erro ao verificar permissão de admin:', rpcError);
        throw rpcError;
      }

      if (!isAdmin) {
        setUser(null);
        return;
      }

      const { data: { user: authUser } } = await supabase.auth.getUser();

      const { data: profile } = await supabase
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', userId)
        .maybeSingle();

      const adminName = profile?.name || authUser?.email?.split('@')[0] || 'Admin';
      const adminAvatarUrl = profile?.avatar_url;

      setUser({
        id: userId,
        email: authUser?.email || '',
        name: adminName,
        avatar_url: adminAvatarUrl,
        is_platform_admin: true
      });

      // Auditoria em fire-and-forget
      logUserLogin(userId, null, {
        email: authUser?.email,
        login_method: 'password',
        is_platform_admin: true,
      }).catch((e) => console.warn('Falha auditoria admin login:', e));

      if (!getWelcomedStatus()) {
        toast.success('Bem-vindo!', { description: `Você está logado como ${adminName}` });
        setWelcomedStatus(true);
      }
    } catch (error) {
      console.error('❌ Error checking admin status:', error);
      setUser(null);
    } finally {
      isCheckingRef.current = false;
      setIsLoading(false);
    }
  };


  const signOut = async () => {
    // Registrar logout antes de deslogar
    if (user) {
      await logUserLogout(user.id, null);
    }
    
    await supabase.auth.signOut();
    setUser(null);
    setWelcomedStatus(false);
    lastCheckedUserIdRef.current = null;
  };

  return { user, isLoading, signOut };
};
